import { useReadQuery } from '@apollo/client';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { StickyTabNav } from '@synoptic/ui-kit/tab-nav/sticky-tab-nav.js';
import { Suspense } from 'react';
import { ClientLoaderFunction, href, Outlet, useParams } from 'react-router';
import { TournamentFieldsFragmentFragment } from '@/__generated__/graphql.ts';
import { Tournament } from '@/__generated__/schema-types.ts';
import { isAuthenticated } from '@/amplify/is-authenticated.server';
import { makeSsrApolloClient } from '@/apollo/ssr-apollo-client';
import { ErrorPage } from '@/components/error-page/error-page';
import { Page, PageContent } from '@/components/layout/page';
import { PageHeader } from '@/components/page-header/page-header';
import { PageHeaderSearch } from '@/components/page-header/page-header-search';
import { getS3Url } from '@/components/s3/get-s3-url';
import { getTournamentPath } from '@/components/s3/tournament/get-path';
import { TabLink } from '@/components/tab-link';
import {
  TournamentType,
  determinateTournamentType,
} from '@/components/tournaments/determinate-tournament-type';
import { SCRAPPING_TOURNAMENT_ID } from '@/env';
import { featureFlagGuardLoader } from '@/feature-flags/feature-flag-guard-loader.ts';
import { flags } from '@/feature-flags/flags.ts';
import { getPageMeta } from '@/routes-utils/get-page-meta';
import { paths } from '@/routes-utils/paths';
import { Route } from './+types/route';
import { tournamentLoader, useTournamentLoaderData } from './loader';
import { TournamentBannerSection } from './tournament-banner-section';
import { TournamentBannerSkeleton } from './tournament-banner/tournament-banner-skeleton';
import { UnauthenticatedTournamentQuery } from './unauthenticated-tournament-query';
import { tournamentNav } from './page.css';

export const loader = async ({ params, context }: Route.LoaderArgs) => {
  if (await isAuthenticated(context)) {
    return null;
  }

  const id = params.id as string;
  const client = makeSsrApolloClient();

  const { data } = await client.query({
    query: UnauthenticatedTournamentQuery,
    variables: { id },
  });

  return { unauthenticatedData: data };
};

export const clientLoader = featureFlagGuardLoader(tournamentLoader, {
  flag: flags.tournaments,
});
(clientLoader as ClientLoaderFunction).hydrate = true;

export const meta = ({ loaderData }: Route.MetaArgs) => {
  const tournament =
    loaderData &&
    'unauthenticatedData' in loaderData &&
    loaderData.unauthenticatedData?.tournament;

  return tournament
    ? getPageMeta({
        title: tournament.name,
        previewDescription: tournament.headline || '',
        previewImage: tournament.image
          ? getS3Url(
              getTournamentPath(tournament.id, tournament.image.key),
            )?.toString()
          : undefined,
        twitterCard: 'summary',
        url: `https://synoptic.com/p/tournaments/${tournament.id}`,
      })
    : getPageMeta();
};
export const ErrorBoundary = ErrorPage;

export default function TournamentPage() {
  const tournamentId = useParams().id as string;

  const { queryRef, authenticated } = useTournamentLoaderData();
  const {
    data: { tournament },
  } = useReadQuery(queryRef);

  const tournamentType = determinateTournamentType(
    (tournament as Tournament)?.fields as TournamentFieldsFragmentFragment[],
  );
  const isTablet = useIsTablet();

  return (
    <Page>
      {authenticated && (
        <PageHeader>
          Tournament
          <PageHeaderSearch />
        </PageHeader>
      )}

      <PageContent>
        <Suspense fallback={<TournamentBannerSkeleton />}>
          <TournamentBannerSection />
        </Suspense>

        {authenticated && (
          <StickyTabNav
            className={tournamentNav}
            size={isTablet ? 'large' : 'small'}
          >
            <TabLink
              end
              to={paths.makeTournament(tournamentId)}
              preventScrollReset
            >
              Overview
            </TabLink>

            {SCRAPPING_TOURNAMENT_ID === tournamentId && (
              <TabLink
                end
                to={href('/p/tournaments/:id/stats', { id: tournamentId })}
                preventScrollReset
              >
                Live Results
              </TabLink>
            )}

            {tournamentType === TournamentType.Prediction ? (
              <TabLink
                end
                to={paths.makeQuestionnaire(tournamentId)}
                preventScrollReset
              >
                Questionnaire
              </TabLink>
            ) : null}

            <TabLink end to={paths.makeRules(tournamentId)} preventScrollReset>
              Rules
            </TabLink>

            <TabLink
              end
              to={paths.makeSubmissions(tournamentId)}
              preventScrollReset
            >
              Submissions
            </TabLink>

            <TabLink
              end
              to={paths.makeParticipants(tournamentId)}
              preventScrollReset
            >
              Participants
            </TabLink>

            <TabLink
              end
              to={paths.makeDiscussions(tournamentId)}
              preventScrollReset
            >
              Discussions
            </TabLink>
          </StickyTabNav>
        )}
        {authenticated && <Outlet />}
      </PageContent>
    </Page>
  );
}
